import { getRegionLabel } from '@/constants'
import type { StreamSliceNode } from '@/analysis/searchContract'
import type { Dimension } from './rows'
import type { Row } from './types'
import { fmtTimestamp } from './format'
import { explainSeverity } from '@/utils/severityExplain'
import SeverityHex from './SeverityHex'
import HexCell from './HexCell'

type Props = {
  row: Row
  dimension: Dimension
  drillStream: string | null
  onActivate: (row: Row) => void
}

const buildTooltip = (row: Row): string =>
  explainSeverity({
    avgSeverity: row.avgSeverity,
    worstMetric: row.worstMetric,
    worstMetricSeverity: row.worstMetricSeverity ?? null,
    metrics: row.metrics ?? null,
  })

const isClickable = (row: Row, dim: Dimension): boolean =>
  ((dim === 'continent' || dim === 'country' || dim === 'region') && row.kind === 'group') ||
  ((dim === 'studio' || dim === 'game') && row.kind === 'group') ||
  (dim === 'stream' && row.kind === 'stream') ||
  (dim === 'user' && row.kind === 'group')

const streamSid = (row: StreamSliceNode) => String(row.streamId || row.value || '').trim()

export default function StreamRowView({ row, dimension, drillStream, onActivate }: Props) {
  const isStream = row.kind === 'stream'
  const id = isStream ? streamSid(row as StreamSliceNode) : row.value

  const info = isStream
    ? {
        label: (row as StreamSliceNode).label || id,
        country: getRegionLabel((row as StreamSliceNode).country ?? 'GB'),
        url: (row as StreamSliceNode).catalogUrl ?? (row as StreamSliceNode).sourceUrl ?? null,
        streamType: (row as StreamSliceNode).streamType ?? null,
        studio: (row as StreamSliceNode).studio ?? null,
        game: (row as StreamSliceNode).game ?? null,
      }
    : {
        label: row.label,
        country: '—',
        url: null,
        streamType: null,
        studio: null,
        game: null,
      }

  const tooltip = buildTooltip(row)

  const isHighlighted = isStream && drillStream === streamSid(row as StreamSliceNode)
  const clickable = isClickable(row, dimension)

  return (
    <tr
      onClick={clickable ? () => onActivate(row) : undefined}
      style={{
        cursor: clickable ? 'pointer' : 'default',
        background: isHighlighted ? 'var(--bg-2)' : undefined,
      }}
    >
      <td>
        <SeverityHex
          avg={row.avgSeverity}
          worst={row.worstMetricSeverity ?? null}
          tooltip={tooltip}
        />
      </td>
      <td>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ fontWeight: 600, color: 'var(--t-0)' }}>
            {info.label}
          </div>
          {isStream && (
            <div style={{ fontSize: 10, color: 'var(--t-2)' }}>
              {[info.streamType ?? '—', info.studio, info.game, info.country]
                .filter(Boolean)
                .join(' · ')}
            </div>
          )}
          {isStream && (() => {
            const sn = row as StreamSliceNode
            const pt = sn.parentSliceType
            const pv = sn.parentSliceValue
            if (!pt || pt === 'global') return null
            return (
              <div style={{ fontSize: 9, color: 'var(--t-2)', opacity: 0.9 }}>
                {pt}: {pv}
              </div>
            )
          })()}
        </div>
      </td>
      {dimension === 'stream' && (
        <td style={{
          color: 'var(--t-2)',
          maxWidth: 240,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {isStream ? (info.url ?? '—') : '—'}
        </td>
      )}
      {dimension === 'user' && (
        <td style={{ color: 'var(--t-2)', fontFamily: 'var(--font-mono)' }}>
          {isStream ? '—' : id}
        </td>
      )}
      <td style={{ color: 'var(--t-2)' }}>{row.tickCount ?? '—'}</td>
      <td style={{ color: 'var(--t-2)' }}>{row.usersTotal ?? '—'}</td>
      {dimension !== 'stream' && (
        <td style={{ color: 'var(--t-2)' }}>{isStream ? '—' : row.totalStreams}</td>
      )}
      <td style={{ color: 'var(--t-2)', fontSize: 10, fontFamily: 'var(--font-mono)' }}>{fmtTimestamp(row.windowStartMs)}</td>
      <td style={{ color: 'var(--t-2)', fontSize: 10, fontFamily: 'var(--font-mono)' }}>{fmtTimestamp(row.windowEndMs)}</td>
      <td><HexCell row={row} layer="demuxInRate" /></td>
      <td><HexCell row={row} layer="demuxToDecodeLagMs" /></td>
      <td><HexCell row={row} layer="renderInOutGap" /></td>
      <td><HexCell row={row} layer="downloadKbps" /></td>
    </tr>
  )
}
